import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Upload, FileText, CheckCircle2, XCircle,
  Download, AlertTriangle
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { sitesAPI } from '@/lib/api';
import { toast } from 'sonner';

const CSV_TEMPLATE = `domain,brand_name,affiliate_url,main_offer,primary_keywords,primary_color,secondary_color,country,language
betebetgiris.com,Betebet Giris,https://affiliate.example.com/betebet,%200 Hos Geldin Bonusu,"betebet giris,betebet guncel giris",#e91e63,#c2185b,TR,tr
betasusgiris.com,Betasus Giris,https://affiliate.example.com/betasus,%300 Kayip Bonusu,"betasus giris,betasus guncel giris",#4caf50,#388e3c,TR,tr`;

export default function BulkImportPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    if (!f.name.endsWith('.csv')) {
      toast.error('Sadece CSV dosyasi kabul edilir');
      return;
    }
    setFile(f);
    setResult(null);

    // Parse CSV for preview
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        toast.error('CSV en az 1 veri satiri icermeli');
        return;
      }
      const headers = lines[0].split(',').map(h => h.trim());
      const rows = [];
      for (let i = 1; i < lines.length && i <= 11; i++) {
        // Simple CSV parse (handles quoted fields)
        const values = lines[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || [];
        const row = {};
        headers.forEach((h, idx) => {
          row[h] = (values[idx] || '').replace(/^"|"$/g, '').trim();
        });
        rows.push(row);
      }
      setPreview(rows);
    };
    reader.readAsText(f);
  };

  const handleUpload = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const { data } = await sitesAPI.bulkCSV(file);
      setResult(data);
      if (data.created?.length > 0) {
        toast.success(`${data.created.length} site olusturuldu!`);
      }
      if (data.failed?.length > 0) {
        toast.warning(`${data.failed.length} site olusturulamadi`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toplu olusturma basarisiz');
    } finally {
      setLoading(false);
    }
  };

  const handleManualImport = async () => {
    if (preview.length === 0) return;
    setLoading(true);
    try {
      const { data } = await sitesAPI.bulkCreate(preview);
      setResult(data);
      if (data.created?.length > 0) {
        toast.success(`${data.created.length} site olusturuldu!`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Toplu olusturma basarisiz');
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'trafficos_bulk_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 max-w-5xl">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
        <Button variant="ghost" onClick={() => navigate('/sites')} className="gap-2 mb-4 text-muted-foreground">
          <ArrowLeft className="w-4 h-4" /> Geri
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight font-['Space_Grotesk'] mb-1">Toplu Site Olustur</h1>
        <p className="text-sm text-muted-foreground">CSV dosyasi ile birden fazla site olusturun (maks. 10 site/batch)</p>
      </motion.div>

      {/* Upload Area */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}>
        <Card className="border-border/70">
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>CSV Yukle</span>
              <Button variant="ghost" size="sm" onClick={downloadTemplate} className="text-xs text-muted-foreground gap-1.5">
                <Download className="w-3 h-3" /> Sablon Indir
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div
              className="border-2 border-dashed border-border/70 rounded-xl p-8 text-center cursor-pointer hover:border-[hsl(var(--primary))]/40 transition-colors duration-200"
              onClick={() => fileInputRef.current?.click()}
              data-testid="bulk-import-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="bulk-import-file-input"
              />
              <Upload className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground mb-1">
                {file ? file.name : 'CSV dosyanizi secin veya surukleyin'}
              </p>
              <p className="text-xs text-muted-foreground/60">
                Gerekli alanlar: domain, brand_name, affiliate_url
              </p>
            </div>

            {file && !result && (
              <div className="mt-4 flex gap-3">
                <Button
                  onClick={file ? handleUpload : handleManualImport}
                  disabled={loading}
                  className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] press-active"
                  data-testid="bulk-import-submit-button"
                >
                  {loading ? 'Olusturuluyor...' : `${preview.length} Siteyi Taslak Olarak Olustur`}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Preview Table */}
      {preview.length > 0 && !result && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <Card className="border-border/70">
            <CardHeader>
              <CardTitle className="text-base">Onizleme ({preview.length} site)</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-background/70">
                      <TableHead className="text-xs">#</TableHead>
                      <TableHead className="text-xs">Domain</TableHead>
                      <TableHead className="text-xs">Marka</TableHead>
                      <TableHead className="text-xs">Affiliate URL</TableHead>
                      <TableHead className="text-xs">Teklif</TableHead>
                      <TableHead className="text-xs">Durum</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.map((row, idx) => {
                      const valid = row.domain && row.brand_name && row.affiliate_url;
                      return (
                        <TableRow key={idx}>
                          <TableCell className="text-xs text-muted-foreground">{idx + 1}</TableCell>
                          <TableCell className="font-mono text-xs">{row.domain || '-'}</TableCell>
                          <TableCell className="text-sm">{row.brand_name || '-'}</TableCell>
                          <TableCell className="font-mono text-xs max-w-[200px] truncate">{row.affiliate_url || '-'}</TableCell>
                          <TableCell className="text-xs">{row.main_offer || '-'}</TableCell>
                          <TableCell>
                            {valid ? (
                              <span className="badge-aktif text-[10px] px-2 py-0.5 rounded-full">Gecerli</span>
                            ) : (
                              <span className="badge-hata text-[10px] px-2 py-0.5 rounded-full">Eksik</span>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Results */}
      {result && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <Card className="border-border/70">
            <CardHeader>
              <CardTitle className="text-base">Sonuclar</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                  <span className="text-sm font-medium">{result.created?.length || 0} basarili</span>
                </div>
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-400" />
                  <span className="text-sm font-medium">{result.failed?.length || 0} basarisiz</span>
                </div>
              </div>

              {result.created?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Olusturulan Siteler</p>
                  {result.created.map((s, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 rounded-lg bg-emerald-400/5 border border-emerald-400/20">
                      <div>
                        <p className="text-sm font-medium">{s.brand_name}</p>
                        <p className="text-xs font-mono text-muted-foreground">{s.domain}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/sites/${s.site_id}/detail`)} className="text-xs">
                        Detay
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {result.failed?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Basarisiz</p>
                  {result.failed.map((f, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-red-400/5 border border-red-400/20">
                      <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5" />
                      <div>
                        <p className="text-xs text-muted-foreground">Satir {f.index + 1}</p>
                        <p className="text-sm text-red-400">{f.error}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <Button onClick={() => { setResult(null); setFile(null); setPreview([]); }} variant="outline" className="border-border/70">
                  Yeni Yukle
                </Button>
                <Button onClick={() => navigate('/sites')} className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]">
                  Site Yoneticisine Git
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
